export const MFA_METHOD_EMAIL = 0
export const MFA_METHOD_SMS = 1
export const MFA_METHOD_APP = 2

export const STAFF_ACTIVE = 0
export const STAFF_INACTIVE = 99

export const MEMBER_ACTIVE = 0
export const MEMBER_INACTIVE = 99

export const QUEST_ACTIVE = 0
export const QUEST_INACTIVE = 99

export const QCONT_ACTIVE = 0;
export const QCONT_INACTIVE = 99;

export const CAL_ACTIVE = 0;
export const CAL_INACTIVE = 99;

export const DOC_ACTIVE = 0;
export const DOC_INACTIVE = 99;

export const PROV_ACTIVE = 0;
export const PROV_INACTIVE = 99;

export const HOME_ACTIVE = 0;
export const HOME_INACTIVE = 99;

export const UPLOAD_DOC = 0
export const UPLOAD_IMG = 1

export const REQ_ADD_USER = 0
export const REQ_DELETE_USER = 1

export const DOC_VISIBLE = 0
export const DOC_HIDDEN = 1

export const TABLE_DOWNLOAD = 1

export const FILING_UNLOCKED = 0
export const FILING_LOCKED = 1

export const PROG_AVAP = 1
export const PROG_MVB = 2
export const PROG_RIVAP = 3
export const PROG_VT = 4
export const PROG_WAPAL = 5
export const PROG_CHARI = 6
export const PROG_RIPCA = 7

const REMIT_PROGRAM_NAME_LONG: Partial<Record<number, string>> = {
	[PROG_AVAP]: 'Alaska Vaccine Assessment Program',
	[PROG_MVB]: 'Maine Vaccine Board',
	[PROG_RIVAP]: 'Rhode Island Vaccine Assessment Program',
	[PROG_VT]: 'Vermont Vaccine Purchasing Program',
	[PROG_WAPAL]: 'Washington Partnership Access Lines Fund',
	[PROG_CHARI]: "Children's Health Account of Rhode Island",
	[PROG_RIPCA]: 'Rhode Island Primary Care Assessment Fund',
}

// Short names that differ from tbl_programs.prog_name in the email script
const REMIT_PROGRAM_SHORT: Partial<Record<number, string>> = {
	[PROG_WAPAL]: 'WAPAL',
	[PROG_CHARI]: 'CHARI',
	[PROG_RIPCA]: 'RI PCA',
}

const REMIT_PROGRAM_INVOICE_REF: Partial<Record<number, string>> = {
	[PROG_RIPCA]: '("RI PCA")',
}

export type RemitEmailProgram = {
	prog_id: number
	prog_name: string
}

export const normalizeRemitEmailProgram = (program: unknown): RemitEmailProgram | undefined => {
	if (!program || typeof program !== 'object') return undefined

	const { prog_id, prog_name } = program as { prog_id?: unknown; prog_name?: unknown }
	if (prog_id == null || prog_name == null) return undefined

	const normalizedProgId = Number(prog_id)
	if (Number.isNaN(normalizedProgId)) return undefined

	return {
		prog_id: normalizedProgId,
		prog_name: String(prog_name),
	}
}

export const parseRemitEmailPrograms = (data: string): RemitEmailProgram[] => {
	try {
		const parsed = JSON.parse(data) as unknown
		if (!Array.isArray(parsed)) return []

		return parsed
			.map(normalizeRemitEmailProgram)
			.filter((program): program is RemitEmailProgram => program !== undefined)
	} catch {
		return []
	}
}

const getRemitShortName = (program: RemitEmailProgram) =>
	REMIT_PROGRAM_SHORT[program.prog_id] ?? program.prog_name

const getRemitInvoiceRef = (program: RemitEmailProgram) => {
	const shortName = getRemitShortName(program)
	return REMIT_PROGRAM_INVOICE_REF[program.prog_id] ?? `(${shortName})`
}

const SHFT_REBRAND_URL = 'https://wecanshft.org/'
const SHFT_REBRAND_LABEL = 'KIDSVAX IS NOW SHFT | LEARN MORE'
const SHFT_REBRAND_LINK_CLASS = 'shft-rebrand-link'
const SHFT_REBRAND_LINK_STYLE = [
	'color: #467886',
	'text-decoration: underline',
	'text-decoration-color: #00F',
	'-webkit-text-decoration-color: #00F',
	'text-transform: uppercase',
	'font-family: Arial, Helvetica, sans-serif',
].join('; ')
const SHFT_REBRAND_STYLE_BLOCK = `<style>
	.${SHFT_REBRAND_LINK_CLASS} { color: #467886 !important; text-decoration: underline !important; text-decoration-color: #00F !important; -webkit-text-decoration-color: #00F !important; }
	@media (prefers-color-scheme: dark) {
		.${SHFT_REBRAND_LINK_CLASS} { color: #6FA1AF !important; text-decoration-color: #C886FF !important; -webkit-text-decoration-color: #C886FF !important; }
	}
</style>`

const getRipcaRemittanceEmailMessage = () => `Dear Payer,

	Our records indicate that the attached Rhode Island Primary Care Assessment (RI PCA) invoice remains unpaid.

	As previously communicated, the RI PCA Program will sunset effective October 1, 2026. In order to complete the program's final reconciliation and closeout, we kindly request that any outstanding assessment balance be paid as soon as possible. Please use the updated payment instructions included on the attached remittance form when submitting payment.

	If you have already submitted payment, please disregard this notice. Otherwise, please contact accounting@wecanshft.org with any questions or if there is an issue preventing payment.

	Thank you for your prompt attention to this matter.

	Sincerely,
	SHFT
	Service Administrator for RI PCA`

export const getRemittanceEmailMessage = (program?: RemitEmailProgram | null) => {
	const normalized = normalizeRemitEmailProgram(program)
	if (!normalized) return ''

	if (normalized.prog_id === PROG_RIPCA) return getRipcaRemittanceEmailMessage()

	const fullName = REMIT_PROGRAM_NAME_LONG[normalized.prog_id]
	if (!fullName) return ''

	const invoiceRef = getRemitInvoiceRef(normalized)
	const shortName = getRemitShortName(normalized)

	return `Dear Payer,

	Our records indicate that the attached ${fullName} ${invoiceRef} invoice remains unpaid. We kindly request that payment be processed at your earliest convenience.

	If you have already submitted payment, please disregard this notice. Otherwise, feel free to reach out to accounting@wecanshft.org with any questions. Thank you for your prompt attention to this matter.

	Sincerely,
	SHFT
	Service Administrator for ${shortName}`
}

export const formatRemittanceEmailHtml = (message: string) => {
	const body = message.split('\n\n').map((paragraph) => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`).join('')
	return `${body}${SHFT_REBRAND_STYLE_BLOCK}<p><a class="${SHFT_REBRAND_LINK_CLASS}" href="${SHFT_REBRAND_URL}" style="${SHFT_REBRAND_LINK_STYLE}">${SHFT_REBRAND_LABEL}</a></p>`
}

export const BILLING_TYPE_TOTAL_LIVES = 1;
export const BILLING_TYPE_GOAL = 2;
// export const BILLING_TYPES = [BILLING_TYPE_TOTAL_LIVES, BILLING_TYPE_GOAL];
export const BILLING_TYPES = {
    [BILLING_TYPE_TOTAL_LIVES]: 'Total Lives',
    [BILLING_TYPE_GOAL]: 'Goal',
}

export const ZERO_LIVES = 1

export const RATE_TRAINING = 1

export const USER_ACTIVE = 0
export const USER_INACTIVE = 99

export const PAYER_ACTIVE = 0
export const PAYER_INACTIVE = 99

export const PAYER_TYPE_ACTIVE = 0
export const PAYER_TYPE_INACTIVE = 99

export const PAYER_GROUP_ACTIVE = 0
export const PAYER_GROUP_INACTIVE = 99

export const DOMICILE_OUTSIDE_WA = 0
export const DOMICILE_WA = 1

export const FILE_ACTIVE = 0
export const FILE_INACTIVE = 99

export const ADJUST_DEBIT = 0
export const ADJUST_CREDIT = 1

export const REQUEST_REPORTING = 0
export const REQUEST_FILING = 1

export const CAL_FILE_AGENDA = 1
export const CAL_FILE_MINUTES = 2
export const CAL_FILE_MATERIAL = 3

export const NOTIF_MODE_PREVIEW = 0
export const NOTIF_MODE_SEND = 1


export const USERQ_ESCALATE = 0
export const USERQ_ACTIVE = 0
export const USERQ_INACTIVE = 99

export const ZERO_ACTIVE = 1
export const ZERO_INACTIVE = 0

export const GROUP_PAYERS = 1;
export const GROUP_KVUSERS = 2;
export const GROUP_NOTIFICATIONS = 3;

export const MAIL_SEND = 2;

export const FILING_ACTIVE = 0;
export const FILING_INACTIVE = 99;

export const FILING_PENDING_UPLOAD = 0;
export const FILING_UPLOADED = 1;

export const REMINDER_SENT = 'sent';
export const REMINDER_FAILED = 'failed';
export const REMINDER_NO_RECIPIENT = 'no_recipient';
export const REMINDER_SKIPPED = 'skipped';

export const QB_CONNECT_ACTIVE = 0;
export const QB_CONNECT_INACTIVE = 99;

export const SYNCT_AUTO = 0;
export const SYNCT_MANUAL = 1;

export const SYNC_INCOMPLETE = 0;
export const SYNC_COMPLETE = 1;

export const TRIGGER_ERROR = 1;

export const UPLOAD_FAILURE = 0;
export const UPLOAD_SUCCESS = 1;

export const QB_AUTH_SUCCESS = 0;
export const QB_AUTH_FAILURE = 1;
export const QB_AUTH_INITIAL_SUCCESS = 2;

export const QB_PAYER_INSERT = 0;
export const QB_PAYER_UPDATE = 1;

export const SYNC_PAYER = 0;
export const SYNC_RATE = 1;

export enum PayerPreview {
    QB_PAYER_INSERT = 0,
    QB_PAYER_UPDATE = 1
}
export const MONTH_JAN = 1;

export const RATE_MULTIYEAR = 1;